import { useCallback, useRef } from "react";

/**
 * Inner message list scroller for one conversation pane.
 *
 * Owns a per-instance node so AnimatePresence exit cleanup can identify
 * which pane is detaching and must not clear a newer active scroll target.
 */
export function ConversationScrollPane({
  onActiveScrollElementChange,
  onScroll,
  className,
  children,
  "aria-busy": ariaBusy,
}: {
  onActiveScrollElementChange: (
    next: HTMLDivElement | null,
    detaching?: HTMLDivElement | null,
  ) => void;
  onScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  className?: string;
  children: React.ReactNode;
  "aria-busy"?: boolean;
}): React.JSX.Element {
  const ownedNodeRef = useRef<HTMLDivElement | null>(null);

  const setRef = useCallback(
    (node: HTMLDivElement | null): void => {
      if (node != null) {
        ownedNodeRef.current = node;
        onActiveScrollElementChange(node);
        return;
      }
      const detaching = ownedNodeRef.current;
      ownedNodeRef.current = null;
      onActiveScrollElementChange(null, detaching);
    },
    [onActiveScrollElementChange],
  );

  return (
    <div
      ref={setRef}
      onScroll={onScroll}
      className={className}
      aria-busy={ariaBusy}
    >
      {children}
    </div>
  );
}
